/**
 * Camera thumbnails – push-based access to the latest-recording thumbnail the
 * cloud injects into device status as `status.c_thumbnail`.
 */

import { get } from 'svelte/store';

import type { CameraThumbnailStatus } from '../types/camera-status';
import { devices_status } from 'src/stores';
import { real_id } from 'src/helpers/interface_helpers';

/** Receives a device's latest thumbnail, or `undefined` when it has none. */
type CameraThumbnailListener = (entry?: CameraThumbnailStatus) => void;

const listeners = new Map<string, Set<CameraThumbnailListener>>();

/**
 * The device's latest-recording thumbnail as it currently sits in device status.
 *
 * @param deviceId - The device id; normalised internally.
 * @returns The `c_thumbnail` entry, or `undefined` when the camera has none.
 */
export const getCameraThumbnail = (deviceId: string): CameraThumbnailStatus | undefined =>
	get(devices_status)?.[real_id(deviceId)]?.c_thumbnail;

/**
 * Watches one device's latest thumbnail. The listener never fires on registration,
 * so read the current value with {@link getCameraThumbnail} when binding, and use
 * the entry it is handed rather than re-reading the store: the notification can run
 * either side of the status write.
 *
 * @param deviceId - The device to watch; normalised internally.
 * @param listener - Invoked with the device's thumbnail on each status update.
 * @returns The unsubscribe function.
 */
export const watchCameraThumbnail = (
	deviceId: string,
	listener: CameraThumbnailListener,
): (() => void) => {
	const id = real_id(deviceId);
	const forDevice = listeners.get(id) ?? new Set<CameraThumbnailListener>();
	forDevice.add(listener);
	listeners.set(id, forDevice);

	return () => {
		forDevice.delete(listener);
		if (!forDevice.size) listeners.delete(id);
	};
};

/**
 * Delivers a device's thumbnail to its watchers.
 *
 * @remarks
 * Called by `handleCameraStatusUpdate` for a status from any source, which only
 * calls it for a status that carries a thumbnail.
 *
 * @param deviceId - The device the status belongs to; normalised internally.
 * @param entry - The status's `c_thumbnail`, if it carried one.
 */
export const notifyCameraThumbnail = (deviceId: string, entry?: CameraThumbnailStatus): void => {
	const id = real_id(deviceId);
	const forDevice = listeners.get(id);

	forDevice?.forEach((listener) => listener(entry));
};

/**
 * Watches a single device's latest recording. The callback fires whenever the
 * thumbnail starts pointing at a different recording, including at none, with the
 * recording present at registration time as the baseline — so it does not fire for
 * what is already on screen (do the initial load separately).
 *
 * @param deviceId - The device whose latest recording to watch.
 * @param cb - Invoked with the new recording id whenever it changes.
 * @returns The unsubscribe function.
 */
export const onCameraRecordingChange = (
	deviceId: string,
	cb: (recId: string | undefined) => void,
): (() => void) => {
	let lastRecId = getCameraThumbnail(deviceId)?.recordingId;

	return watchCameraThumbnail(deviceId, (entry) => {
		const recId = entry?.recordingId;
		if (recId === lastRecId) return;
		lastRecId = recId;
		cb(recId);
	});
};
